import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, localeTag, tl } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Clock, CalendarDays, ListChecks, MessageSquare, GraduationCap, ClipboardCheck, Target, ArrowRightLeft, AlertTriangle } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const money = (v: number | null) => v == null ? "—" : fmtMoney(v);

export default function MyDay() {
  const {
    t: tI18n
  } = useI18n();

  const [d, setD] = useState<any>(null);
  useEffect(() => { api.get("/myday").then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return <LoadingBlock />;
  if (d.error) return <PageHeader title="My Day" subtitle="Could not load your day." testId="myday-header" />;
  const s = d.sections;
  const hrs = s.schedule?.operating_hours;
  const firstName = d.user?.display_name ? d.user.display_name.split(" ")[0] : "";
  const dayDate = new Date(d.as_of).toLocaleDateString(localeTag(), { weekday: "long", month: "short", day: "numeric" });
  return (
    (<div data-testid="my-day">
      <PageHeader title={firstName ? tI18n("Good day, {name}", { name: firstName }) : tI18n("Good day")}
        subtitle={d.store ? tI18n("Store #{number} · {date}", { number: d.store.number, date: dayDate }) : dayDate}
        testId="myday-header" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {/* Clock + today's shift */}
        <Card className="p-4" data-testid="myday-schedule">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><Clock size={16} className="text-brand" />{tI18n("Today")}</div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{tI18n("Clock status")}</span>
            <span className={`text-sm font-medium ${s.clock?.state === "clocked_in" ? "text-status-good" : "text-muted-foreground"}`} data-testid="myday-clock">{(s.clock?.state || "unknown").replace("_", " ")}</span>
          </div>
          <div className="mt-2 text-sm">
            {s.schedule?.shift
              ? <span data-testid="myday-shift">{tI18n("Shift")}{" "}{s.schedule.shift.start}–{s.schedule.shift.end}{s.schedule.shift.role ? ` · ${s.schedule.shift.role}` : ""}</span>
              : <span className="text-muted-foreground" data-testid="myday-shift">{tI18n("No shift scheduled today")}</span>}
          </div>
          {hrs?.configured && <div className="mt-1 text-xs text-muted-foreground">{hrs.closed ? <span className="text-status-warn">{tI18n("Store closed")}{hrs.label ? ` · ${hrs.label}` : ""}</span> : `${tl("Store hours")} ${hrs.store_open}–${hrs.store_close}`}</div>}
          <Link to="/my-schedule" className="mt-2 inline-block text-xs text-brand hover:underline" data-testid="myday-link-schedule">{tI18n("View my schedule →")}</Link>
        </Card>

        {/* Shift ops */}
        <Card className="p-4" data-testid="myday-shiftops">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><ArrowRightLeft size={16} className="text-brand" />{tI18n("Shift opportunities")}</div>
          <Row label="Open shifts to claim" value={s.shift_ops?.open_shifts_available} />
          <Row label="Pending swaps" value={s.shift_ops?.pending_swaps} />
          <Row label="Time-off requests" value={s.shift_ops?.pending_time_off} />
        </Card>

        {/* Tasks */}
        <Card className="p-4" data-testid="myday-tasks">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><ListChecks size={16} className="text-brand" />{tI18n("Tasks due today")}</div>
          <div className="flex items-baseline gap-2"><span className="text-2xl font-semibold">{s.tasks?.due_today ?? 0}</span>{s.tasks?.overdue > 0 && <span className="text-xs text-status-critical">{s.tasks.overdue}{" "}{tI18n("overdue")}</span>}</div>
          <div className="mt-1 space-y-0.5">
            {(s.tasks?.items || []).slice(0, 4).map((t: any) => <div key={t.instance_id} className="flex items-center justify-between text-xs"><span className="truncate text-muted-foreground">{t.title}</span>{t.overdue && <AlertTriangle size={12} className="text-status-critical" />}</div>)}
          </div>
          <Link to="/act" className="mt-2 inline-block text-xs text-brand hover:underline" data-testid="myday-link-tasks">{tI18n("Open tasks →")}</Link>
        </Card>

        {/* Messages */}
        <Card className="p-4" data-testid="myday-messages">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><MessageSquare size={16} className="text-brand" />{tI18n("Messages")}</div>
          <Row label="Unread" value={s.messages?.unread} />
          <Row label="Required reads" value={s.messages?.required_reads} critical={s.messages?.required_reads > 0} />
          <Link to="/communicate" className="mt-2 inline-block text-xs text-brand hover:underline" data-testid="myday-link-messages">{tI18n("Open inbox →")}</Link>
        </Card>

        {/* Learning */}
        <Card className="p-4" data-testid="myday-learning">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><GraduationCap size={16} className="text-brand" />{tI18n("Learning")}</div>
          <Row label="Courses due" value={s.learning?.due} critical={s.learning?.due > 0} />
          <Link to="/learn" className="mt-2 inline-block text-xs text-brand hover:underline" data-testid="myday-link-learning">{tI18n("Go to learning →")}</Link>
        </Card>

        {/* Visits / audits / actions */}
        <Card className="p-4" data-testid="myday-assure">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><ClipboardCheck size={16} className="text-brand" />{tI18n("Visits & actions")}</div>
          <Row label="Open corrective actions" value={s.assure?.open_actions} />
          <Row label="Overdue actions" value={s.assure?.overdue_actions} critical={s.assure?.overdue_actions > 0} />
        </Card>

        {/* KPI snapshot (managers) */}
        {s.kpi?.available && (
          <Card className="p-4 md:col-span-2 xl:col-span-3" data-testid="myday-kpi">
            <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><Target size={16} className="text-brand" />{tI18n("KPI snapshot · Actual vs Forecast vs Goal")}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {s.kpi.metrics.map((m: any) => (
                <div key={m.metric} className="rounded-md border border-border bg-muted/20 p-2.5" data-testid={`myday-kpi-${m.metric}`}>
                  <div className="text-xs font-semibold capitalize">{m.metric}</div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-center text-sm">
                    <div><div className="text-[10px] uppercase text-muted-foreground">{tI18n("Actual")}</div><div className="font-semibold">{m.metric === "sales" ? money(m.actual) : (m.actual ?? "—")}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">{tI18n("Forecast")}</div><div className="font-semibold text-brand">{m.metric === "sales" ? money(m.forecast) : (m.forecast ?? "—")}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">{tI18n("Goal (period)")}</div><div className="font-semibold">{m.metric === "sales" ? money(m.goal_period) : (m.goal_period ?? "—")}</div></div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/intelligence" className="mt-2 inline-block text-xs text-brand hover:underline" data-testid="myday-link-kpi">{tI18n("Goals & Targets →")}</Link>
          </Card>
        )}
      </div>
    </div>)
  );
}

const Row: React.FC<{ label: string; value: any; critical?: boolean }> = ({ label, value, critical }) => (
  <div className="flex items-center justify-between border-b border-border/30 py-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-medium ${critical ? "text-status-critical" : ""}`}>{value ?? 0}</span>
  </div>
);
